import { IconAlertTriangle } from 'featherico';
import moment from 'moment';
import { useEffect, useState } from 'react';
import type CompanyPaymentDto from '../../../../types/CompanyPaymentDto';
import type { CurrentUserDto } from '../../../../types/UserDtos';
import AlertBox from '../../components/AlertBox';
import CompanyDateTime from '../../components/DateTime/CompanyDateTime';
import { useAppContext } from '../../hooks/useAppContext';
import replacement from '../../services/Replacement';
import { getAdminBillingUrl } from '../Billing/services/status';

const isVIP = (user: CurrentUserDto) =>
    user.isHR || user.isAssistant || (!user.isGuest && !user.managerId && !!user.reports.length);

const isOnFreePlan = (payment: CompanyPaymentDto) => payment.plan === 0;
const isAboveFreeLimit = (payment: CompanyPaymentDto) => payment.userCount > payment.freeLimit;
const isTriallingBeyondFreeLimit = (payment: CompanyPaymentDto) => isOnFreePlan(payment) && isAboveFreeLimit(payment);
const isTrialExpired = (payment: CompanyPaymentDto) => payment.trialUntil && Date.now() > payment.trialUntil;

const useTrialExpiryInfo = (user: CurrentUserDto): [number, number | null, string | null | undefined] | null => {
    const [checkoutUrl, setCheckoutUrl] = useState<string | null>();

    useEffect(() => {
        getAdminBillingUrl(user).then(setCheckoutUrl);
    }, [user]);

    const { company } = user;
    const { payment } = company;

    if (!isVIP(user) || !isTriallingBeyondFreeLimit(payment) || !isTrialExpired(payment)) {
        return null;
    }

    const daysSinceSignup = moment().diff(moment(company.dateCreated), 'days');
    const daysSinceTrialExpired = payment.trialUntil ? moment().diff(payment.trialUntil, 'days') : null;

    return [daysSinceSignup, daysSinceTrialExpired, checkoutUrl];
};

const useLicenseExpiryInfo = (user: CurrentUserDto): [number, number, number, string | null | undefined] | null => {
    const [checkoutUrl, setCheckoutUrl] = useState<string | null>();

    useEffect(() => {
        getAdminBillingUrl(user).then(setCheckoutUrl);
    }, [user]);

    if (user.company.onAutomatedBilling) {
        return null;
    }

    const { company } = user;
    const { payment } = company;

    if (!isVIP(user) || !payment.paidTo || !payment.paidFrom || isOnFreePlan(payment) || !isAboveFreeLimit(payment)) {
        return null;
    }

    const daysUntilExpiry = moment(payment.paidTo).diff(moment(), 'days');

    if (daysUntilExpiry > payment.warnBeforePaymentExpiry) {
        return null;
    }

    return [daysUntilExpiry, payment.paidFrom, payment.paidTo, checkoutUrl];
};

const TrialExpiredWarning = () => {
    const { user } = useAppContext();
    const expiryInfo = useTrialExpiryInfo(user);
    if (!expiryInfo) {
        return null;
    }

    const [daysSinceSignup, daysSinceTrialExpired, checkoutUrl] = expiryInfo;

    return (
        <AlertBox
            title="Your trial has expired"
            colour={daysSinceTrialExpired && daysSinceTrialExpired > 55 ? 'RED' : 'YELLOW'}
        >
            Your trial started {daysSinceSignup} days ago. Please contact{' '}
            <a href="mailto:support@small-improvements.com">support</a> to extend your trial or to discuss renewal
            options.{' '}
            {!!checkoutUrl && (
                <>
                    <br /> Check out our <a href={checkoutUrl}>checkout page</a> for the details and discounts!
                </>
            )}
        </AlertBox>
    );
};

const LicenseWillExpireWarning = () => {
    const { user } = useAppContext();
    const expiryInfo = useLicenseExpiryInfo(user);

    if (!expiryInfo) {
        return null;
    }

    const [daysUntilExpiry, paidFrom, paidTo, checkoutUrl] = expiryInfo;

    return (
        <AlertBox
            colour={daysUntilExpiry < -28 ? 'RED' : 'YELLOW'}
            title={`Your license ${daysUntilExpiry >= 0 ? 'is about to expire' : 'has expired!'}`}
        >
            Your {replacement('company')} license is valid from <CompanyDateTime date={paidFrom} format="shortDate" />{' '}
            to <CompanyDateTime date={paidTo} format="shortDate" />. Please{' '}
            {checkoutUrl && (
                <>
                    {' '}
                    have a look at the <a href={checkoutUrl}>checkout page</a> or
                </>
            )}{' '}
            contact <a href="mailto:support@small-improvements.com">support</a> to discuss purchasing options.
        </AlertBox>
    );
};

const LicenseExpiredBanner = () => {
    const { user } = useAppContext();
    const expiryInfo = useLicenseExpiryInfo(user);
    if (!expiryInfo) {
        return null;
    }

    const [daysUntilExpiry, , , checkoutUrl] = expiryInfo;

    if (daysUntilExpiry > -28) {
        return null;
    }

    return (
        <div className="boxWarning broadcast">
            <div>
                <p>
                    <IconAlertTriangle />
                    Your license expired {Math.abs(daysUntilExpiry)} days ago!
                </p>
                After 60 days your users will no longer be able to log in! Please{' '}
                {checkoutUrl && (
                    <>
                        have a look at the <a href={checkoutUrl}>checkout page</a> or{' '}
                    </>
                )}
                contact <a href="mailto:support@small-improvements.com">support</a> to discuss purchasing options.
            </div>
        </div>
    );
};

export { LicenseExpiredBanner, LicenseWillExpireWarning, TrialExpiredWarning };
